// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import { object, string, bool, func, array } from 'prop-types';
import { Button, InlineLoading, Loading, Search, SkeletonText } from '@carbon/react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import uniqueId from 'lodash/uniqueId';
import { useContextSelector } from 'use-context-selector';
import { Search as SearchIcon, X, Package, ShoppingCart, Pencil, Trash, FilePlus2 } from 'lucide-react';

import Img from 'Containers/Image/Image';
import useNumberUtils from 'Hooks/useNumberUtils';
import useScreen from 'Hooks/useScreen';
import usePermissions from 'Hooks/usePermissions';
import useUser from 'Hooks/useUser';
import { PERMISSION } from 'Constants';
import { ModalsContext } from 'Contexts/ModalsContext';

import ProductDetailWishlistItem from 'Containers/Products/ProductDetailWishlistItem';
import WishlistsDropdown from 'Containers/Wishlists/WishlistsDropdown';
import EditWishlistModal from 'Containers/Wishlists/EditWishlistModal';
import PromptModal from 'Containers/PromptModal/PromptModal';

import { stockAlertWishlistId } from 'Contexts/WishlistContext';
import { StyledOverlay } from 'Components/Products/Products';
import ProductSkeletonItem from 'Components/Products/ProductSkeletonItem';
import NotFound from 'Components/NotFound/NotFound';

import CsvIcon from 'Assets/images/csv.svg';

const StyledWishlistPage = styled.div`
  position: relative;
`;

const StyledHeading = styled.div`
  > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 15px;
    margin-bottom: 15px;
    border-bottom: 1px solid ${ props => props.theme.products.colors.border };
    color: ${ props => props.theme.colors.darkGray };
  }

  .listInfo {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 80px;
    padding: 0 15px;
    background: linear-gradient(90deg, #040F2A 0%, #041B54 100%);

    .cds--list-box__wrapper {
      margin: 0 50px 0 0;
      max-width: 600px;
      width: 100%;
    }

    .cds--list-box {
      margin: 0;
    }

    .date {
      color: ${ props => props.theme.colors.white };
    }
  }

  .cds--skeleton__text {
    max-width: 250px;
  }

  h1 {
    font-size: 1.875rem;
  }

  p {
    font-size: 0.875rem;

    span {
      font-weight: 600;
    }
  }

  .primary {
    min-height: 0;
    height: 40px;
    width: 200px;
    justify-content: center;
  }

  @media screen and (max-width: 1024px) {
    > div {
      h1 {
        font-size: 24px;
        font-weight: 600;
      }

      .primary {
        font-size: 16px;
        border: none;
        width: auto;
        height: auto;

        span {
          font-size: 24px;
          padding-right: 5px;
        }

        &:hover {
          background-color: transparent;
        }
      }
    }

    .listInfo {
      flex-direction: column;
      align-items: start;
      gap: 10px;
      height: auto;
      padding: 15px;

      .cds--list-box__wrapper {
        margin: 0;
        max-width: none;
      }
    }
  }
`;

const StyledToolbar = styled.div`
  > div {
    &:first-of-type {
      display: flex;
      align-content: center;
      justify-content: space-between;
      padding-bottom: 15px;
      border-bottom: 1px solid ${ props => props.theme.products.colors.border };

      > div {
        display: flex;
        align-content: center;
      }

      .cds--skeleton__text {
        max-width: 150px;
      }
    }

    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 25px;
  }

  .title {
    display: flex;
    flex-direction: column;
    justify-content: center;
    font-size: 1.5rem;
    line-height: 1.5rem;
    color: ${ props => props.theme.colors.darkGray };

    span {
      font-size: 14px;
      color: ${ props => props.theme.colors.gray };
    }
  }

  .search {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    position: relative;
    overflow: visible;

    form {
      display: flex;
    }

    .newProductSearch {
      height: 35px;
      min-width: 300px;
      max-width: 400px;
      width: 100%;
      z-index: 100;

      input {
        border: 1px solid ${ props => props.theme.products.colors.border };
        height: 100%;
        padding: 0 30px 0 40px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }

      .cds--search-close {
        :before {
          display: none;
        }

        height: 100%;
        width: 30px;
        border: 0;
        outline: 0;
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
      }
    }

    .addNewProduct {
      width: 150px;
      height: 35px;
      min-height: 0;
      margin-left: 10px;
      padding: 0;
      color: ${ props => props.theme.colors.blue };
      font-size: 1rem;
      justify-content: center;
    }
  }

  .edit {
    margin-left: 12px;
  }

  .downloadCSV {
    position: relative;
    display: flex;
    align-items: center;
    margin-right: 24px;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      right: 0;
      transform: translateY(-50%);
      width: 1px;
      height: 24px;
      background: #e2e2e2;
    }

    .downloadBtn {
      color: #3a3a3a;
      border: none;

      &:hover,
      &:active,
      &:focus {
        background: transparent;
        outline: none;
        border: none;
        box-shadow: none;
      }
    }

    svg {
      margin: 0 24px;
      width: 27px;
    }
  }

  .generateOffer + .downloadCSV {
    margin-left: 12px;
  }

  .generateOffer:disabled {
    background: transparent;
    color: ${ props => props.theme.colors.gray };

    &:hover,
    &:focus {
      background: transparent;
    }

    svg {
      stroke: currentColor;
    }
  }

  .edit,
  .delete {
    svg {
      position: relative;
      top: -2px;
      margin-right: 5px;
      fill: none;
    }
    height: 40px;
    line-height: 40px;
    font-size: 1rem;
    color: ${ props => props.theme.colors.blue };
    background-color: transparent !important;
  }

  @media screen and (max-width: 1024px) {
    > div:first-of-type {
      flex-direction: column;
      border-bottom: none;
      margin-bottom: 0;
    }

    .mobileHeader {
      width: 100%;
      justify-content: space-between;
      align-items: center;

      .title {
        width: calc(100vw - 120px);
        font-size: 18px;

        > div {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
  
        span {
          font-size: 14px;
        }
      }

      > div {
        display: flex;

        .downloadCSV {
          margin: 0;

          &::after {
            content: none;
          }

          svg {
            margin: 0 10px;
          }
        }

        .searchExistingProduct {
          position: relative;

          .searchExistingProductBtn {
            padding: 10px;
            border: 1px solid #e2e2e2;
          }

          .existingProductSearchContainer {
            position: absolute;
            padding: 10px;
            background: #fff;
            top: 100%;
            right: 0;
            width: calc(100vw - 32px);
            z-index: 1;
            box-shadow: 0px 0px 6px #00000029;

            .existingProductSearch {
              display: block;
              max-width: none;
            }
          }
        }
      }
    }

    .mobileActionButtons {
      width: 100%;
      margin-top: 10px;
      justify-content: space-around;
      gap: 10px;

      button {
        flex-grow: 1;
        background: #F5F7F9 !important;
        margin: 0;

        :disabled {
          color: #8d8d8d;
        }
      }
    }

    .search {
      width: 100%;

      form {
        justify-content: space-between;
        width: 100%;
      }

      .newProductSearch {
        flex-grow: 1;
        max-width: none;
        min-width: auto;
      }
    }
  }
`;

const StyledProducts = styled.div`
  > p.error {
    color: ${ props => props.theme.colors.default };
  }

  .noProducts {
    margin: 5px 0 15px;
  }

  .wishlistProductSkeletonItem {
    padding-bottom: 20px;
    margin: 0 0 25px 0;
    border-bottom: 1px solid ${ props => props.theme.products.colors.border };

    > div:first-of-type {
      padding-left: 0;
    }

    > div:last-of-type {
      padding-right: 0;
    }
  }

  .productListItem {
    position: relative;

    &:last-of-type {
      border-bottom: 0;
    }
  }
`;

const StyledExistingSearch = styled(Search)`
  height: 35px;
  max-width: 300px;
  width: 100%;

  .cds--search-magnifier {
    display: ${ props => props.value.trim().length ? 'none' : 'block' };
  }

  .cds--search-close {
    &:before {
      display: none;
    }

    &:hover svg {
      fill: ${ props => props.theme.colors.default };
    }

    outline: 0 !important;
    border: 0 !important;
    background-color: transparent !important;
  }

  .cds--search-magnifier-icon {
    left: auto;
    right: 1rem;
    fill: ${ props => props.theme.colors.default }
  }

  input {
    height: 100% !important;
    padding: 0 35px 0 15px !important;
    border: 1px solid ${ props => props.theme.products.colors.border };
    border-radius: 5px;
  }

  .cds--search-close {
    height: 100%;
  }

  @media screen and (max-width: 1024px) {
    display: none;
  }
`;

const StyledResultsContainer = styled.div`
  display: ${ props => props.open ? 'block' : 'none' };
  max-height: 400px;
  width: 100%;
  padding: 0 10px;
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 100;
  overflow: auto;
  background-color: ${ props => props.theme.colors.white };
  box-shadow: 0 0 8px #00000029;

  .cds--loading {
    margin: 10px 0;
  }
`;

const StyledResearchItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 5px auto;
  padding: 5px 0;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${ props => props.theme.products.colors.border };
  }

  .info {
    width: 100%;
    display: flex;
    align-items: center;

    .preview {
      min-width: 40px;
      width: 40px;
      height: 40px;
      padding: 5px;
      margin-right: 5px;

      img {
        display: block;
        height: auto;
        max-width: 100%;
        max-height: 100%;
      }

      svg {
        display: block;
        margin: 0 auto;
        height: auto;
        width: auto;
        max-height: 100%;
        max-width: 100%;
      }
    }
  }

  .details {
    padding-right: 10px;
    font-size: 0.875rem;
    color: ${ props => props.theme.colors.darkGray };

    p {
      font-size: inherit;
      line-height: 1.25rem;
      color: inherit;
    }

    .code {
      font-size: 0.75rem;
      font-weight: 600;

      span {
        font-weight: normal;
      }
    }
  }

  .title {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 0.875rem;
  }

  .selectProduct {
    height: 30px;
    padding: 0 10px;
    font-size: 0.75rem;
  }
`;

const LoadingOverlay = styled(StyledOverlay)`
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.65);
  z-index: 101;

  .loading {
    justify-content: center;

    svg:not(.cds--inline-loading--error) {
      transform: scale(2);
    }
  }
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  .addToCart {
    padding: 0;
    height: 40px;
    width: 255px;
    justify-content: center;

    svg {
      margin-right: 6px;
      height: 16px;
    }
  }
`;

const StyledSearchOverlay = styled(StyledOverlay)`
  display: ${ props => props.open ? 'block' : 'none' };
  position: absolute;
  left: 0;
  top: 0;
  z-index: 99;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.75);
`;

const WishlistsComponent = props => {
  const {
    onChangeQty,
    wishlist,
    loading,
    products,
    quantities,
    onToggleSearch,
    addToCartLoading,
    onAddProduct,
    onAddToCart,
    searchResults,
    loadingSearch,
    searchOpen,
    existingProductSearch,
    selectProduct,
    selectedProduct,
    newProductSearch,
    onClear,
    onKeyDown,
    onChangeInputValue,
    onChangeList,
    onDeleteList,
    onWishlistDownload,
    onGenerateOffer,
    showExistingProductsSearch,
    setShowExistingProductsSearch,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { formatDate } = useNumberUtils();
  const { width } = useScreen();
  const { can } = usePermissions();
  const { data: { isImpersonated } = {} } = useUser();
  const canAddToCart = can(PERMISSION.ADD_TO_CART);
  const canAccessOffers = !isImpersonated && can(PERMISSION.ACCESS_ACCOUNT_FEATURES);
  const {
    createWishlistModal,
    editWishlistModal,
    deleteWishlistModal,
    onOpenModal,
    onCloseModal
  } = useContextSelector(ModalsContext, data => ({
    editWishlistModal: data.modalState.EDIT_WISHLIST,
    createWishlistModal: data.modalState.CREATE_WISHLIST,
    deleteWishlistModal: data.modalState.DELETE_WISHLIST,
    onOpenModal: data.openModal,
    onCloseModal: data.closeModal
  }));

  const memoizedItems = useMemo(
    () => {
      if (wishlist) {
        if (wishlist?.products?.length) {
          return (products || []).map(product => (
            <ProductDetailWishlistItem
              key={ uniqueId(product.id) }
              product={ {
                ...product,
                title: product.name,
                quantity: product.quantity || 0,
                stockValue: product.stockValue,
                minQuantity: product.minQuantity
              } }
              parentWishlist={ wishlist }
              onChangeQty={ onChangeQty }
            />
          ));
        }
        return (
          <p className="noProducts">{ t('wishlists.empty') }</p>
        );
      }
      return null;
    },
    [wishlist, products]
  );

  if (!wishlist?.id && !loading) {
    return (
      <NotFound
        title={ t('common.oops') }
        description={ t('wishlists.listNotFound') }
        buttonText={ t('wishlists.goToMainList') }
        url="/lists"
      />
    );
  }

  return (
    <>
      <EditWishlistModal
        modalOpen={ editWishlistModal.state.open }
        onCloseModal={ () => onCloseModal(editWishlistModal.key) }
        wishlist={ wishlist }
      />
      <PromptModal
        modalOpen={ deleteWishlistModal.state.open }
        onCloseModal={ () => onCloseModal(deleteWishlistModal.key) }
        onConfirm={ onDeleteList }
        header={ <h4>{ t('wishlists.deleteList') }</h4> }
        body={ <p>{ t('wishlists.confirmDelete') }</p> }
        className="DeleteWishlistModal"
      />
      <StyledWishlistPage theme={ theme }>
        <StyledSearchOverlay open={ searchOpen } onClick={ onToggleSearch(false) } />
        <LoadingOverlay open={ loading }>
          { loading && (
            <InlineLoading
              className="loading"
              status="active"
              description=""
            />
          ) }
        </LoadingOverlay>
        <StyledHeading theme={ theme }>
          <div className="header">
            <h1>{ t('wishlists.lists') }</h1>
            <Button
              kind="ghost"
              className="primary blue"
              onClick={ () => onOpenModal(createWishlistModal.key) }
            >
              + { t('wishlists.newList') }
            </Button>
          </div>
          <div className="listInfo">
            <WishlistsDropdown onChangeList={ onChangeList } id={ wishlist?.id } />
            { wishlist?.updatedAt
              ? (
                <p className="date">
                  { t('common.lastUpdated') }: <span>{ formatDate(wishlist.updatedAt) }</span>
                </p>
              )
              : null}
          </div>
        </StyledHeading>
        <StyledProducts theme={ theme }>
          <>
            <StyledToolbar theme={ theme }>
              <div>
                { !wishlist
                  ? <SkeletonText />
                  : (
                    <>
                      { width > 1024 ? (
                        <>
                          <div>
                            <h3 className="title">
                              { wishlist.name }
                              { wishlist.isDefault && <span>{ t('wishlists.defaultList') }</span> }
                            </h3>
                            <Button
                              kind="ghost"
                              className="edit"
                              disabled={ wishlist.id === stockAlertWishlistId }
                              onClick={ () => onOpenModal(editWishlistModal.key) }
                            >
                              <Pencil size={ 18 } /> { t('wishlists.editList') }
                            </Button>
                          </div>
                          <div>
                            { canAccessOffers && (
                              <Button
                                kind="ghost"
                                className="generateOffer"
                                disabled={ !wishlist.products?.length || wishlist.id === stockAlertWishlistId }
                                onClick={ onGenerateOffer }
                              >
                                <FilePlus2 size={ 18 } /> { t('offers.generateOffer') }
                              </Button>
                            ) }
                            <div className="downloadCSV">
                              <Button
                                kind="ghost"
                                className="downloadBtn"
                                onClick={ onWishlistDownload }
                              >
                                { t('wishlists.downloadList') }: <CsvIcon />
                              </Button>
                            </div>
                            <Button
                              kind="ghost"
                              className="delete"
                              disabled={ wishlist.isDefault || wishlist.id === stockAlertWishlistId }
                              onClick={ () => onOpenModal(deleteWishlistModal.key) }
                            >
                              <Trash size={ 18 } /> { t('wishlists.deleteList') }
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mobileHeader">
                            <h3 className="title">
                              <div>{ wishlist.name }</div>
                              { wishlist.isDefault && <span>{ t('wishlists.defaultList') }</span> }
                            </h3>
                            <div>
                              <div className="downloadCSV">
                                <Button
                                  kind="ghost"
                                  className="downloadBtn"
                                  onClick={ onWishlistDownload }
                                >
                                  <CsvIcon />
                                </Button>
                              </div>
                              <div className="searchExistingProduct">
                                <Button
                                  kind="ghost"
                                  className="searchExistingProductBtn"
                                  onClick={ () => setShowExistingProductsSearch(!showExistingProductsSearch) }
                                >
                                  { showExistingProductsSearch ? <X size={20} /> : <SearchIcon size={20} /> }
                                </Button>
                                { showExistingProductsSearch && (
                                  <div className="existingProductSearchContainer">
                                    <StyledExistingSearch
                                      className="existingProductSearch"
                                      disabled={ loading || !wishlist }
                                      theme={ theme }
                                      id={ uniqueId('wishlistExistingSearch') }
                                      labelText=""
                                      light
                                      value={ existingProductSearch }
                                      placeholder={ t('products.searchProduct') }
                                      onChange={ onChangeInputValue('existing') }
                                    />
                                  </div>
                                ) }
                              </div>
                            </div>
                          </div>
                          <div className="mobileActionButtons">
                            { canAccessOffers && (
                              <Button
                                kind="ghost"
                                className="generateOffer"
                                disabled={ !wishlist.products?.length || wishlist.id === stockAlertWishlistId }
                                onClick={ onGenerateOffer }
                              >
                                <FilePlus2 size={ 18 } /> { t('offers.generateOffer') }
                              </Button>
                            ) }
                            <Button
                              kind="ghost"
                              className="edit"
                              disabled={ wishlist.id === stockAlertWishlistId }
                              onClick={ () => onOpenModal(editWishlistModal.key) }
                            >
                              <Pencil /> { t('wishlists.editList') }
                            </Button>
                            <Button
                              kind="ghost"
                              className="delete"
                              disabled={ wishlist.isDefault || wishlist.id === stockAlertWishlistId }
                              onClick={ () => onOpenModal(deleteWishlistModal.key) }
                            >
                              <Trash size={ 18 } /> { t('wishlists.deleteList') }
                            </Button>
                          </div>
                        </>
                      )}
                    </>
                  )}
              </div>
              <div>
                <div className="search">
                  <form>
                    <Search
                      required
                      className="newProductSearch"
                      theme={ theme }
                      onClear={ onClear }
                      id={ uniqueId('wishlistNewSearch') }
                      disabled={ loading || !wishlist }
                      labelText={ t('products.addNew') }
                      light
                      onKeyDown={ onKeyDown }
                      placeholder={ t('common.search.placeholder') }
                      value={ selectedProduct?.title || newProductSearch }
                      onClick={ onToggleSearch(true) }
                      onChange={ onChangeInputValue('new') }
                    />
                    <Button
                      kind="ghost"
                      type="submit"
                      onClick={ onAddProduct }
                      className="addNewProduct"
                    >
                      + { t('products.addNew') }
                    </Button>
                  </form>
                  <StyledResultsContainer theme={ theme } open={ searchOpen }>
                    { loadingSearch && <Loading active withOverlay={ false } small /> }
                    { searchResults.map(product => (
                      <StyledResearchItem key={ product.id } theme={ theme }>
                        <div className="info">
                          <div className="preview">
                            <Img
                              src={ product?.pictures?.at(0)?.urlOverlayPicture }
                              unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
                              loading="lazy"
                              loader={ <Loading active withOverlay={ false } small /> }
                              alt={ product.title }
                            />
                          </div>
                          <div className="details">
                            <p className="title">{ product.title }</p>
                            <p className="code">{ t('product.code') }:<span>{ product.code }</span>
                            </p>
                          </div>
                        </div>
                        <Button
                          className="selectProduct blue"
                          kind="ghost"
                          onClick={ () => selectProduct(product) }
                        >
                          { t('products.select') }
                        </Button>
                      </StyledResearchItem>
                    )) }
                  </StyledResultsContainer>
                </div>
                <StyledExistingSearch
                  className="existingProductSearch"
                  disabled={ loading || !wishlist }
                  theme={ theme }
                  id={ uniqueId('wishlistExistingSearch') }
                  labelText=""
                  light
                  value={ existingProductSearch }
                  placeholder={ t('products.searchProduct') }
                  onChange={ onChangeInputValue('existing') }
                />
              </div>
            </StyledToolbar>
            { !wishlist
              ? Array.from({ length: 4 }, () => (
                <ProductSkeletonItem
                  className="wishlistProductSkeletonItem"
                  key={ uniqueId('wishlistProductItem') }
                />
              ))
              : memoizedItems}
          </>
        </StyledProducts>
        <StyledButtonContainer>
          <Button
            className="addToCart red"
            kind="danger"
            disabled={
              loading
              || !canAddToCart
              || addToCartLoading
              || !wishlist
              || !products.length
              || !Object
                .values(quantities)
                .filter(prod => (
                  prod.quantity >= prod.minQuantity
                  && prod.quantity <= prod.stockValue
                ))
                .length
              || Object
                .values(products)
                .filter(product => (
                  product.warnings?.blocked_prices
                  || product.warnings?.blocked_stocks
                  || product.warnings?.blocked_orders
                  || product.hasPriceRequest
                ))
                .length
            }
            onClick={ onAddToCart }
            data-test="onAddToCartProductItemWishlistPage"
            data-testid={ (products || []).map(prod => prod.id).join(',') }
          >
            { addToCartLoading && (
              <Loading active={ addToCartLoading } small withOverlay={ false } />
            ) }
            <ShoppingCart />
            { t('wishlists.addToCart') }
          </Button>
        </StyledButtonContainer>
      </StyledWishlistPage>
    </>
  );
};

WishlistsComponent.propTypes = {
  onChangeQty: func,
  wishlist: object,
  quantities: object,
  searchResults: array,
  products: array,
  loading: bool,
  addToCartLoading: bool,
  loadingSearch: bool,
  searchOpen: bool,
  onAddToCart: func,
  onAddProduct: func,
  newProductSearch: string,
  existingProductSearch: string,
  selectedProduct: object,
  selectProduct: func,
  onClear: func,
  onToggleSearch: func,
  onWishlistDownload: func,
  onGenerateOffer: func,
  onKeyDown: func,
  onChangeInputValue: func,
  onChangeList: func,
  onDeleteList: func,
  showExistingProductsSearch: bool,
  setShowExistingProductsSearch: func,
};
WishlistsComponent.displayName = 'WishlistsComponent';

export default WishlistsComponent;
